import { Injectable, OnDestroy, computed, inject, signal } from '@angular/core';
import { Observable, Subject, Subscription, catchError, debounceTime, distinctUntilChanged, finalize, map, of, switchMap, throwError } from 'rxjs';
import { EjecutivoCementoApiService } from '../services/ejecutivo-cemento-api.service';
import { EjecutivoCementoUpsertCommand, EjecutivoCementoComunaOption, EjecutivoCementoRegionOption } from '../models/ejecutivo-cemento.model';
import { EjecutivoCementoVm } from '../models/ejecutivo-cemento.vm';
import { EjecutivoCementoMapper } from '../pages/ejecutivos/mappers/ejecutivo-cemento.mapper';

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_ORDERING = 'nombre';

@Injectable()
export class EjecutivosCementoStore implements OnDestroy {
  private readonly api = inject(EjecutivoCementoApiService);
  private readonly loadingDepth = signal(0);
  private readonly searchChanges$ = new Subject<string>();
  private readonly searchSubscription: Subscription;

  readonly items = signal<EjecutivoCementoVm[]>([]);
  readonly regions = signal<EjecutivoCementoRegionOption[]>([]);
  readonly geographicItems = signal<EjecutivoCementoComunaOption[]>([]);
  readonly totalItems = signal(0);
  readonly page = signal(DEFAULT_PAGE);
  readonly pageSize = signal(DEFAULT_PAGE_SIZE);
  readonly searchTerm = signal('');
  readonly ordering = signal(DEFAULT_ORDERING);
  readonly activeFilter = signal<'all' | 'true' | 'false'>('all');
  readonly error = signal<string | null>(null);
  readonly loading = computed(() => this.loadingDepth() > 0);
  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalItems() / this.pageSize())));
  readonly hasPreviousPage = computed(() => this.page() > 1);
  readonly hasNextPage = computed(() => this.page() < this.totalPages());
  readonly pageStartIndex = computed(() => this.totalItems() ? (this.page() - 1) * this.pageSize() + 1 : 0);
  readonly pageEndIndex = computed(() => Math.min(this.totalItems(), this.page() * this.pageSize()));

  constructor() {
    this.searchSubscription = this.searchChanges$.pipe(
      debounceTime(350),
      distinctUntilChanged(),
      switchMap(() => this.loadItemsInternal(false)),
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  loadItems(): Observable<void> {
    return this.loadItemsInternal(true);
  }

  loadCatalog(): Observable<void> {
    return this.execute(this.api.loadGeographicCatalog().pipe(
      map((items) => {
        const active = items.filter((item) => item.activo);
        const regionsByCode = new Map<string, EjecutivoCementoRegionOption>();
        const comunas: EjecutivoCementoComunaOption[] = [];
        for (const item of active) {
          regionsByCode.set(item.codigoRegion, {
            codigo: item.codigoRegion,
            nombre: item.nombreRegion,
          });
          comunas.push({
            id: Number(item.id),
            codigo: item.codSapComuna,
            nombre: item.nombreComuna,
            regionCode: item.codigoRegion,
          });
        }
        this.regions.set(Array.from(regionsByCode.values()).sort((a, b) => a.nombre.localeCompare(b.nombre)));
        this.geographicItems.set(comunas.sort((a, b) => a.nombre.localeCompare(b.nombre)));
        this.error.set(null);
      }),
      catchError((error) => {
        this.error.set('No se pudo cargar el catálogo territorial.');
        return throwError(() => error);
      }),
    ));
  }

  setSearchTerm(value: string): void {
    const normalized = String(value ?? '');
    if (this.searchTerm() === normalized) return;
    this.searchTerm.set(normalized);
    this.page.set(DEFAULT_PAGE);
    this.searchChanges$.next(normalized);
  }

  setActiveFilter(value: 'all' | 'true' | 'false'): void {
    if (this.activeFilter() === value) return;
    this.activeFilter.set(value);
    this.page.set(DEFAULT_PAGE);
    this.loadItemsInternal(false).subscribe();
  }

  setOrdering(value: string): void {
    const normalized = value.trim() || DEFAULT_ORDERING;
    if (this.ordering() === normalized) return;
    this.ordering.set(normalized);
    this.page.set(DEFAULT_PAGE);
    this.loadItemsInternal(false).subscribe();
  }

  setPage(value: number): void {
    const page = Number.isFinite(value) ? Math.max(1, Math.trunc(value)) : DEFAULT_PAGE;
    if (this.page() === page) return;
    this.page.set(page);
    this.loadItemsInternal(false).subscribe();
  }

  setPageSize(value: number): void {
    const pageSize = Number.isFinite(value) ? Math.max(1, Math.trunc(value)) : DEFAULT_PAGE_SIZE;
    if (this.pageSize() === pageSize) return;
    this.pageSize.set(pageSize);
    this.page.set(DEFAULT_PAGE);
    this.loadItemsInternal(false).subscribe();
  }

  createItem(command: EjecutivoCementoUpsertCommand): Observable<void> {
    return this.execute(this.api.createItem(command).pipe(catchError((error) => {
      this.error.set('No se pudo crear el ejecutivo.');
      return throwError(() => error);
    })));
  }

  updateItem(id: number, command: EjecutivoCementoUpsertCommand): Observable<void> {
    return this.execute(this.api.updateItem(id, command).pipe(catchError((error) => {
      this.error.set('No se pudo actualizar el ejecutivo.');
      return throwError(() => error);
    })));
  }

  deactivateItem(id: number): Observable<void> {
    return this.execute(this.api.deactivateItem(id).pipe(catchError((error) => {
      this.error.set('No se pudo desactivar el ejecutivo.');
      return throwError(() => error);
    })));
  }

  clearError(): void { this.error.set(null); }

  private loadItemsInternal(propagateError: boolean): Observable<void> {
    return this.execute(this.api.listItems({
      page: this.page(), pageSize: this.pageSize(), search: this.searchTerm().trim(),
      ordering: this.ordering(), activo: this.activeFilter(),
    }).pipe(
      map((response) => {
        this.totalItems.set(Math.max(0, Number(response.count) || 0));
        this.items.set(EjecutivoCementoMapper.toVmList(response.results));
        this.error.set(null);
      }),
      catchError((error) => {
        this.items.set([]); this.totalItems.set(0);
        this.error.set('No se pudieron cargar los ejecutivos.');
        return propagateError ? throwError(() => error) : of(undefined);
      }),
    ));
  }

  private execute(source$: Observable<void>): Observable<void> {
    this.loadingDepth.update((depth) => depth + 1);
    return source$.pipe(finalize(() => this.loadingDepth.update((depth) => Math.max(0, depth - 1))));
  }
}
